import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { supabase } from '../../lib/supabase';
import type { Property } from '../../lib/supabase';
import AiMatchModal from './components/AiMatchModal';
import TenantWaitingModal from './components/TenantWaitingModal';
import SeoHead from '../../components/feature/SeoHead';
import { getHourlyFakeBase } from '../../lib/hourlyCount';
import LatestPropertiesSection from './components/LatestPropertiesSection';
import RentalIncomeCounter from './components/RentalIncomeCounter';
import LatestUploadsFeed from './components/LatestUploadsFeed';
import HeroLiveStats from './components/HeroLiveStats';
import MobileHero from './components/MobileHero';
import DesktopHero from './components/DesktopHero';
import LoggedInHome from './components/LoggedInHome';
import { useMobileTheme } from '../../hooks/useMobileTheme';
import { useAuth } from '../../hooks/useAuth';
import GuestOnboardingModal from './components/GuestOnboardingModal';
import MobilePropertiesSection from './components/MobilePropertiesSection';
import BetaFeedbackButton from '../../components/feature/BetaFeedbackButton';
import JoinUsSection from './components/JoinUsSection';

const PLACEHOLDER_IMAGES = [
  'https://public.readdy.ai/ai/img_res/04d86ac4e8b2bc4bebf646b2da0faef6.jpg',
  'https://readdy.ai/api/search-image?query=cozy%20living%20room%20apartment%20warm%20lighting%20comfortable%20furniture%20clean%20simple%20interior%20professional%20real%20estate%20photo%20neutral%20background&width=600&height=400&seq=ph2&orientation=landscape',
  'https://readdy.ai/api/search-image?query=contemporary%20apartment%20bedroom%20natural%20light%20clean%20design%20simple%20furniture%20white%20walls%20professional%20photography&width=600&height=400&seq=ph3&orientation=landscape',
];

function getPlaceholderImage(id: string) {
  const idx = id.charCodeAt(0) % PLACEHOLDER_IMAGES.length;
  return PLACEHOLDER_IMAGES[idx];
}

export default function Home() {
  const [latestProperties, setLatestProperties] = useState<Property[]>([]);
  const [loadingLatest, setLoadingLatest] = useState(true);
  const [showAiMatchModal, setShowAiMatchModal] = useState(false);
  const [showWaitingModal, setShowWaitingModal] = useState(false);
  const [showGuestOnboarding, setShowGuestOnboarding] = useState(false);
  const [tenantMatchCount, setTenantMatchCount] = useState<number | null>(null);
  const navigate = useNavigate();
  const { theme } = useMobileTheme();
  const { user, loading: authLoading } = useAuth();

  // 桌機版 section 主題色
  const secAlt = theme === 'pink' ? 'bg-rose-50' : theme === 'white' ? 'bg-gray-50' : 'bg-stone-50';
  const secAltBorder = theme === 'pink' ? 'border-rose-100' : theme === 'white' ? 'border-gray-100' : 'border-stone-200';
  const accentLabel = theme === 'pink' ? 'text-rose-400' : theme === 'white' ? 'text-teal-600' : 'text-teal-600';
  const iconBtnBg = theme === 'pink' ? 'bg-rose-400 group-hover:bg-rose-500' : theme === 'white' ? 'bg-gray-800 group-hover:bg-gray-900' : 'bg-teal-500 group-hover:bg-teal-600';
  const cardHoverBorder = theme === 'pink' ? 'hover:border-rose-200' : theme === 'white' ? 'hover:border-gray-300' : 'hover:border-teal-200';
  const stepNumBg = theme === 'pink' ? 'bg-rose-400' : theme === 'white' ? 'bg-gray-800' : 'bg-teal-500';
  const stepIcon = theme === 'pink' ? 'text-rose-400' : theme === 'white' ? 'text-gray-700' : 'text-teal-500';
  const ctaBtn = theme === 'pink' ? 'bg-rose-400 hover:bg-rose-500' : theme === 'white' ? 'bg-gray-900 hover:bg-gray-800' : 'bg-teal-500 hover:bg-teal-400';

  useEffect(() => {
    const fetchLatest = async () => {
      setLoadingLatest(true);
      const { data } = await supabase
        .from('properties')
        .select('*')
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(8);
      setLatestProperties(data || []);
      setLoadingLatest(false);
    };

    setTenantMatchCount(getHourlyFakeBase());
    fetchLatest();

    const msToNextHour = (60 * 60 * 1000) - (Date.now() % (60 * 60 * 1000));
    let hourTimer: ReturnType<typeof setTimeout>;
    const scheduleHourly = (delay: number) => {
      hourTimer = setTimeout(() => {
        setTenantMatchCount(getHourlyFakeBase());
        scheduleHourly(60 * 60 * 1000);
      }, delay);
    };
    scheduleHourly(msToNextHour);
    return () => clearTimeout(hourTimer);
  }, []);

  // If logged in, show logged-in homepage
  if (!authLoading && user) {
    return (
      <>
        <SeoHead
          title="藍舍AI地產｜台北 新北 桃園 台中 台南 高雄 零仲介費屋主直接溝通"
          description="藍舍AI地產－台灣六都最大零仲介費屋主直租平台。台北市、新北市、桃園市、台中市、台南市、高雄市房源直租，AI輔助出租自動撰稿配對，省下高額仲介費，屋主直租零手續費。"
          keywords="藍舍AI地產,台北租屋,新北租屋,桃園租屋,台中租屋,台南租屋,高雄租屋,屋主直租,零仲介費"
          canonical="/"
        />
        <Navbar />
        <LoggedInHome user={user} />
        <Footer />
        <BetaFeedbackButton />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="藍舍AI地產｜台北 新北 桃園 台中 台南 高雄 零仲介費屋主直接溝通"
        description="藍舍AI地產－台灣六都最大零仲介費屋主直租平台。台北市、新北市、桃園市、台中市、台南市、高雄市房源直租，AI輔助出租自動撰稿配對，省下高額仲介費，屋主直租零手續費。"
        keywords="藍舍AI地產,台北租屋,新北租屋,桃園租屋,台中租屋,台南租屋,高雄租屋,屋主直租,零仲介費"
        canonical="/"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "藍舍AI地產 - 台灣六都零仲介費屋主直租",
            "url": `${import.meta.env.VITE_SITE_URL || 'https://ailanshe.com'}/`,
            "description": "台北、新北、桃園、台中、台南、高雄六都零仲介費屋主直租平台，AI輔助出租自動撰稿配對",
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "首頁", "item": `${import.meta.env.VITE_SITE_URL || 'https://ailanshe.com'}/` }
              ]
            }
          }
        ]}
      />
      <Navbar />
      {/* ── HERO 手機版：三主題可切換 ── */}
      <MobileHero
        tenantMatchCount={tenantMatchCount}
        onShowWaitingModal={() => setShowWaitingModal(true)}
        onShowGuestOnboarding={() => setShowGuestOnboarding(true)}
      />

      {/* ── 手機版即時房源（防詐信任區塊） ── */}
      <MobilePropertiesSection
        properties={latestProperties}
        loading={loadingLatest}
        onPublish={() => setShowGuestOnboarding(true)}
      />

      {/* ── HERO 桌機版：三主題可切換 ── */}
      <DesktopHero
        tenantMatchCount={tenantMatchCount}
        onShowWaitingModal={() => setShowWaitingModal(true)}
        onShowGuestOnboarding={() => setShowGuestOnboarding(true)}
      />

      {/* ── 桌機專屬內容區塊（手機隱藏，減少載入） ── */}
      <div className="hidden lg:block">

        {/* ── 1. 最新上架房源 ── */}
        <LatestPropertiesSection properties={latestProperties} loading={loadingLatest} onPublish={() => setShowGuestOnboarding(true)} />

        {/* ── 2. AI 地產管家 ── */}
        <section className="relative py-20 md:py-28 overflow-hidden bg-gray-950">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=abstract%20dark%20luxury%20real%20estate%20technology%20background%20with%20glowing%20teal%20green%20light%20lines%20and%20geometric%20patterns%2C%20futuristic%20AI%20data%20visualization%2C%20deep%20dark%20navy%20background%20with%20subtle%20golden%20light%20accents%2C%20premium%20modern%20architecture%20silhouette%2C%20cinematic%20dramatic%20lighting%2C%20ultra%20high%20quality%20digital%20art&width=1920&height=900&seq=aimanager_bg1&orientation=landscape"
              alt=""
              loading="lazy"
              className="w-full h-full object-cover object-top opacity-25"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-gray-900/90 to-teal-950/80"></div>
          </div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-teal-500/8 rounded-full blur-3xl pointer-events-none"></div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
              <div className="flex-1 text-center lg:text-left">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-teal-500/20 border border-teal-400/30 text-teal-300 rounded-full text-xs font-bold mb-6">
                  <i className="ri-sparkling-2-fill text-amber-400 animate-pulse"></i>
                  屋主專屬 · AI 智能管理
                </div>
                <h2 className="text-4xl md:text-5xl font-black text-white mb-5 leading-tight">
                  AI 地產管家<br />
                  <span className="text-teal-400">讓資產替你工作</span>
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-10 max-w-xl mx-auto lg:mx-0">
                  {[
                    { icon: 'ri-line-chart-line', text: '即時收益追蹤與 APR 計算' },
                    { icon: 'ri-robot-2-line', text: 'AI 市場行情自動分析' },
                    { icon: 'ri-building-4-line', text: '多房源集中管理' },
                    { icon: 'ri-calculator-line', text: '投資報酬率試算工具' },
                    { icon: 'ri-notification-3-line', text: '租客訊息即時通知' },
                    { icon: 'ri-pie-chart-2-line', text: '資產增值視覺化報表' },
                  ].map((item) => (
                    <div key={item.text} className="flex items-center gap-3 bg-white/5 rounded-xl px-4 py-3 border border-white/10">
                      <div className="w-6 h-6 flex items-center justify-center bg-teal-500/20 rounded-lg flex-shrink-0">
                        <i className={`${item.icon} text-xs text-teal-400`}></i>
                      </div>
                      <span className="text-xs text-white/80 font-medium">{item.text}</span>
                    </div>
                  ))}
                </div>
                <RentalIncomeCounter />
                <div className="flex flex-col sm:flex-row items-center lg:items-start gap-3 mt-8">
                  <Link to="/landlord-dashboard"
                    className="inline-flex items-center gap-3 px-7 py-3.5 bg-teal-500 hover:bg-teal-400 text-white rounded-2xl font-bold text-sm shadow-xl transition-all hover:-translate-y-0.5 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-dashboard-3-line text-base"></i>
                    立即進入 AI 地產管家
                    <i className="ri-arrow-right-line text-sm"></i>
                  </Link>
                  <Link to="/register"
                    className="inline-flex items-center gap-2 px-6 py-3.5 bg-white/10 hover:bg-white/15 text-white rounded-2xl font-medium text-sm border border-white/20 transition-all cursor-pointer whitespace-nowrap"
                  >
                    免費註冊開始使用
                  </Link>
                </div>
              </div>

              <div className="flex-shrink-0 w-full lg:w-[400px]">
                <div className="bg-white/8 backdrop-blur-xl rounded-3xl border border-white/15 overflow-hidden shadow-2xl">
                  <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-white/5">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 flex items-center justify-center bg-teal-500 rounded-lg">
                        <i className="ri-dashboard-3-line text-white text-sm"></i>
                      </div>
                      <div>
                        <p className="text-white text-sm font-bold">AI 地產管家</p>
                        <p className="text-white/50 text-xs">即時資產總覽</p>
                      </div>
                    </div>
                    <span className="flex items-center gap-1.5 text-xs text-teal-400">
                      <span className="w-1.5 h-1.5 bg-teal-400 rounded-full animate-pulse"></span>
                      即時更新
                    </span>
                  </div>
                  <div className="px-5 py-5">
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      {[
                        { label: '本月租金收入', value: 'NT$ 68,000', change: '+12%', icon: 'ri-money-dollar-circle-line', color: 'text-teal-400' },
                        { label: '平均年化報酬', value: '5.8% APR', change: '+0.3%', icon: 'ri-percent-line', color: 'text-amber-400' },
                        { label: '管理中房源', value: '4 間', change: '招租中 2', icon: 'ri-building-4-line', color: 'text-sky-400' },
                        { label: '資產總市值', value: '2,850 萬', change: '+180萬', icon: 'ri-line-chart-line', color: 'text-green-400' },
                      ].map((item) => (
                        <div key={item.label} className="bg-white/5 rounded-2xl p-3 border border-white/10">
                          <div className="flex items-center gap-1.5 mb-2">
                            <i className={`${item.icon} text-sm ${item.color}`}></i>
                            <span className="text-white/50 text-xs">{item.label}</span>
                          </div>
                          <p className="text-white font-black text-base mb-0.5">{item.value}</p>
                          <p className="text-xs text-green-400 font-semibold flex items-center gap-0.5">
                            <i className="ri-arrow-up-s-line text-sm"></i>{item.change}
                          </p>
                        </div>
                      ))}
                    </div>
                    <div className="space-y-2">
                      <p className="text-white/40 text-xs font-medium uppercase tracking-wider mb-1">房源狀態</p>
                      {[
                        { name: '台北市大安區 2房', status: '招租中', statusColor: 'text-green-400 bg-green-400/15', rent: '28,000' },
                        { name: '新北市板橋區 套房', status: '已租出', statusColor: 'text-amber-400 bg-amber-400/15', rent: '15,000' },
                        { name: '台中市西屯區 3房', status: '招租中', statusColor: 'text-green-400 bg-green-400/15', rent: '25,000' },
                      ].map((p) => (
                        <div key={p.name} className="flex items-center justify-between bg-white/5 rounded-xl px-3 py-2.5 border border-white/8">
                          <span className="text-white/80 text-xs truncate mr-2">{p.name}</span>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${p.statusColor}`}>{p.status}</span>
                            <span className="text-white/50 text-xs whitespace-nowrap">NT$ {p.rent}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="px-5 pb-5">
                    <Link to="/landlord-dashboard" className="w-full flex items-center justify-center gap-2 py-3 bg-teal-500/80 hover:bg-teal-500 text-white text-sm font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-sparkling-2-fill text-amber-300"></i>
                      查看完整 AI 分析報告
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── 3. 零仲介費屋主直接溝通 ── */}
        <section className={`py-14 md:py-20 bg-white border-t ${secAltBorder}`}>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 md:mb-14 gap-4">
              <div>
                <p className={`text-xs ${accentLabel} uppercase tracking-wider mb-1.5`}>為什麼選擇我們</p>
                <h2 className="text-2xl md:text-4xl font-black text-gray-900 leading-tight">
                  零仲介費<br className="hidden md:block" />直接省下高額費用
                </h2>
              </div>
              <p className="text-sm text-gray-500 max-w-xs">省下高額仲介費，AI 輔助讓出租更簡單，屋主與租客直接聯繫，安心又透明。</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
              {[
                { icon: 'ri-money-dollar-circle-line', title: '零仲介費', desc: '屋主直接發布，租客直接聯繫，省下高額仲介費用，讓租房更經濟實惠。' },
                { icon: 'ri-sparkling-2-line', title: 'AI 輔助出租', desc: 'AI 智能撰寫房源描述、自動配對租客，簡化出租流程，輕鬆達成出租目標。' },
                { icon: 'ri-shield-check-line', title: '真實可靠', desc: 'AI 輔助審核房源資訊，如發現違規或不實內容將予以移除，讓每一次看房都能更安心。' },
              ].map((item) => (
                <div key={item.title} className={`bg-white rounded-2xl p-6 md:p-8 border border-stone-100 ${cardHoverBorder} transition-colors group`}>
                  <div className={`w-12 h-12 flex items-center justify-center ${iconBtnBg} rounded-xl mb-4 transition-colors`}>
                    <i className={`${item.icon} text-2xl text-white`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
            <LatestUploadsFeed />
          </div>
        </section>

        {/* ── 4. 如何使用平台 ── */}
        <section className="py-14 md:py-20 bg-white">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">簡單三步驟</p>
              <h2 className="text-2xl md:text-4xl font-black text-gray-900">如何使用平台</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 md:gap-12 relative">
              <div className="hidden sm:block absolute top-10 left-1/3 right-1/3 h-px bg-stone-200 z-0"></div>
              {[
                { step: '01', icon: 'ri-user-add-line', title: '免費註冊', desc: '建立帳號，選擇屋主或租客身份，完全免費，一分鐘搞定' },
                { step: '02', icon: 'ri-home-4-line', title: '發布或搜尋', desc: '屋主發布房源，租客依地區、房型、關鍵字搜尋理想住所' },
                { step: '03', icon: 'ri-chat-3-line', title: '直接聯繫', desc: '雙方直接溝通，省去仲介中間環節，快速達成租約' },
              ].map((item) => (
                <div key={item.step} className="text-center relative z-10">
                  <div className="relative inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-white rounded-2xl border border-stone-200 mb-5 mx-auto">
                    <i className={`${item.icon} text-2xl md:text-3xl ${stepIcon}`}></i>
                    <span className={`absolute -top-2.5 -right-2.5 w-7 h-7 ${stepNumBg} text-white text-xs font-bold rounded-full flex items-center justify-center`}>{item.step}</span>
                  </div>
                  <h3 className="text-base md:text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 5. 通證系統區塊已隱藏，等候喚醒 ── */}

        {/* ── 6. 全台六都服務 ── */}
        <section className={`py-14 md:py-20 bg-white border-t ${secAltBorder}`}>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <p className="text-xs text-teal-600 uppercase tracking-wider mb-1.5">全台六都服務</p>
              <h2 className="text-2xl md:text-4xl font-black text-gray-900 mb-3">
                台北・新北・桃園・台中・台南・高雄
              </h2>
              <p className="text-sm text-gray-500">六都零仲介費房源，直接聯繫省大錢，AI 智能配對加速找到理想租客或租屋</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mb-10">
              {[
                { city: '台北市', slug: 'taipei', icon: 'ri-building-4-line', districts: ['大安區','信義區','中山區','內湖區'], color: 'from-teal-500 to-teal-600', bgColor: '#f0fdfa', borderColor: '#99f6e4', text: 'text-teal-700', gradFrom: '#14b8a6', gradTo: '#0d9488' },
                { city: '新北市', slug: 'newtaipei', icon: 'ri-city-line', districts: ['板橋區','三重區','新莊區','新店區'], color: 'from-cyan-500 to-cyan-600', bgColor: '#ecfeff', borderColor: '#06b6d4', text: 'text-cyan-700', gradFrom: '#06b6d4', gradTo: '#0891b2' },
                { city: '桃園市', slug: 'taoyuan', icon: 'ri-home-office-line', districts: ['桃園區','中壢區','龜山區','蘆竹區'], color: 'from-sky-500 to-sky-600', bgColor: '#f0f9ff', borderColor: '#7dd3fc', text: 'text-sky-700', gradFrom: '#0ea5e9', gradTo: '#0284c7' },
                { city: '台中市', slug: 'taichung', icon: 'ri-building-2-line', districts: ['西屯區','北屯區','南屯區','豐原區'], color: 'from-emerald-500 to-emerald-600', bgColor: '#ecfdf5', borderColor: '#6ee7b7', text: 'text-emerald-700', gradFrom: '#10b981', gradTo: '#059669' },
                { city: '台南市', slug: 'tainan', icon: 'ri-ancient-gate-line', districts: ['東區','永康區','安南區','仁德區'], color: 'from-amber-500 to-amber-600', bgColor: '#fffbeb', borderColor: '#fcd34d', text: 'text-amber-700', gradFrom: '#f59e0b', gradTo: '#d97706' },
                { city: '高雄市', slug: 'kaohsiung', icon: 'ri-ship-2-line', districts: ['苓雅區','三民區','鳳山區','左營區'], color: 'from-rose-500 to-rose-600', bgColor: '#fff1f2', borderColor: '#fda4af', text: 'text-rose-700', gradFrom: '#f43f5e', gradTo: '#e11d48' },
              ].map((item) => (
                <Link
                  key={item.city}
                  to={`/regions/${item.slug}`}
                  className={`group relative rounded-2xl p-4 hover:-translate-y-1 transition-all duration-200 cursor-pointer overflow-hidden`}
                  style={{ backgroundColor: item.bgColor, border: `2px solid ${item.borderColor}` }}
                >
                  <div
                    className="absolute top-0 left-0 right-0 h-1"
                    style={{ background: `linear-gradient(to right, ${item.gradFrom}, ${item.gradTo})` }}
                  ></div>
                  <div
                    className={`w-10 h-10 flex items-center justify-center rounded-xl mb-3`}
                    style={{ background: `linear-gradient(135deg, ${item.gradFrom}, ${item.gradTo})` }}
                  >
                    <i className={`${item.icon} text-white text-lg`}></i>
                  </div>
                  <h3 className={`font-black text-base ${item.text} mb-2`}>{item.city}</h3>
                  <div className="space-y-1">
                    {item.districts.map(d => (
                      <p key={d} className="text-xs text-gray-500 flex items-center gap-1">
                        <i className="ri-map-pin-2-line text-gray-300 text-xs flex-shrink-0"></i>
                        <span>{d}租屋</span>
                      </p>
                    ))}
                  </div>
                  <div className={`mt-3 flex items-center gap-1 text-xs font-semibold ${item.text} group-hover:gap-2 transition-all`}>
                    <span>查看房源</span>
                    <i className="ri-arrow-right-line text-xs"></i>
                  </div>
                </Link>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2 justify-center">
              <span className="text-xs text-gray-400 mr-1">熱門搜尋：</span>
              {[
                '台北市租屋','新北市租屋','桃園市租屋','台中市租屋','台南市租屋','高雄市租屋',
                '板橋租屋','中壢租屋','大安區租屋','西屯區租屋','永康區租屋','三民區租屋',
                '零仲介費租屋','免仲介費',
              ].map((kw) => (
                <Link key={kw} to="/regions"
                  className="px-2.5 py-1 bg-stone-100 hover:bg-teal-50 hover:text-teal-700 text-gray-500 text-xs rounded-full border border-stone-200 hover:border-teal-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {kw}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="relative py-20 md:py-28 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=warm%20cozy%20home%20interior%20with%20soft%20natural%20sunlight%20streaming%20through%20large%20windows%2C%20comfortable%20modern%20living%20room%20with%20plants%20and%20wooden%20furniture%2C%20inviting%20peaceful%20atmosphere%2C%20professional%20interior%20design%20photography%2C%20warm%20golden%20hour%20lighting%20creating%20welcoming%20ambiance&width=1920&height=800&seq=cta1&orientation=landscape"
              alt="CTA Background"
              loading="lazy"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-black/35"></div>
          </div>
          <div className="relative z-10 text-center px-4">
            <h2 className="text-3xl md:text-5xl font-black text-white mb-4 md:mb-6">開始您的直租之旅</h2>
            <p className="text-sm md:text-base text-white/80 mb-8 md:mb-10">無需仲介，直接溝通，省錢省心，安全可靠</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => setShowGuestOnboarding(true)}
                className={`inline-flex items-center gap-3 px-7 py-3.5 ${ctaBtn} text-white rounded-full font-bold text-sm shadow-xl transition-all hover:-translate-y-0.5 cursor-pointer whitespace-nowrap`}
              >
                <i className="ri-home-4-line text-base"></i>立即發布房源
              </button>
              <Link to="/regions"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-white/15 hover:bg-white/25 text-white rounded-full font-medium text-sm border border-white/30 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-search-line text-sm"></i>瀏覽所有房源
              </Link>
            </div>
            <p className="text-xs text-white/40 mt-6">目前平台處於測試（Beta）階段，功能持續優化中，服務方案保留調整權利</p>
          </div>
        </section>

        {/* ── 安全聲明 ── */}
        <section className={`py-12 md:py-16 ${secAlt} border-t ${secAltBorder}`}>
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row gap-6">
              <div className="flex-1 bg-white rounded-2xl border border-stone-200 p-6">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 flex items-center justify-center bg-amber-500 rounded-xl border border-amber-100">
                    <i className="ri-shield-check-line text-lg text-amber-500"></i>
                  </div>
                  <div>
                    <h4 className="text-base font-black text-gray-900">平台免責聲明</h4>
                    <p className="text-xs text-gray-400">請詳閱重要說明</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {[
                    { icon: 'ri-git-branch-line', text: '本平台為媒合平台，不介入任何租賃交易，目前處於測試（Beta）階段。' },
                    { icon: 'ri-lock-password-line', text: '個人資訊受嚴格保護，不對外公開或提供第三方。' },
                    { icon: 'ri-money-dollar-circle-line', text: '平台唯一目的是協助屋主與租客節省高額仲介費。' },
                    { icon: 'ri-customer-service-2-line', text: '法律疑問請洽詢 AI 助理或專業代書諮詢。' },
                  ].map((item) => (
                    <div key={item.text} className="flex items-start gap-2.5 text-xs text-gray-500 leading-relaxed">
                      <i className={`${item.icon} text-teal-500 text-sm flex-shrink-0 mt-0.5`}></i>
                      <span>{item.text}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-5 pt-4 border-t border-stone-100 flex justify-between items-center">
                  <p className="text-xs text-gray-400">使用本平台即同意上述聲明</p>
                  <Link to="/contact" className="text-xs text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1 cursor-pointer whitespace-nowrap">
                    聯繫官方 <i className="ri-arrow-right-line text-xs"></i>
                  </Link>
                </div>
              </div>

              <div className="flex-1 bg-white rounded-2xl border border-red-100 p-6">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 flex items-center justify-center bg-red-500 rounded-xl border border-red-100">
                    <i className="ri-spy-line text-lg text-red-500"></i>
                  </div>
                  <div>
                    <h4 className="text-base font-black text-gray-900">常見詐騙警示</h4>
                    <p className="text-xs text-red-400">認識詐騙，保護自己</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {[
                    { icon: 'ri-bank-card-line', text: '看房前絕不匯款 — 任何理由要求預付款皆為詐騙。' },
                    { icon: 'ri-links-line', text: '勿點不明連結 — 偽造付款頁面會盜取帳戶資訊。' },
                    { icon: 'ri-user-unfollow-line', text: '簽約前核實身份 — 確認屋主與房屋產權一致。' },
                    { icon: 'ri-price-tag-3-line', text: '低於行情需警惕 — 明顯偏低租金往往是陷阱。' },
                  ].map((item) => (
                    <div key={item.text} className="flex items-start gap-2.5 text-xs text-gray-500 leading-relaxed">
                      <i className={`${item.icon} text-red-400 text-sm flex-shrink-0 mt-0.5`}></i>
                      <span>{item.text}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-5 pt-4 border-t border-red-50 flex items-center justify-between">
                  <p className="text-xs text-gray-400">遇可疑情況立即停止並檢舉</p>
                  <div className="flex items-center gap-2">
                    <a href="tel:165" className="text-xs text-red-500 font-bold flex items-center gap-1 cursor-pointer whitespace-nowrap hover:text-red-600">
                      <i className="ri-phone-line text-xs"></i>撥打 165
                    </a>
                    <span className="text-gray-200">|</span>
                    <Link to="/contact" className="text-xs text-gray-500 hover:text-gray-700 font-medium flex items-center gap-1 cursor-pointer whitespace-nowrap">
                      立即檢舉 <i className="ri-flag-line text-xs"></i>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

      </div>{/* end 桌機專屬 */}

      <JoinUsSection />

      <Footer />

      <AiMatchModal isOpen={showAiMatchModal} onClose={() => setShowAiMatchModal(false)} />
      <TenantWaitingModal isOpen={showWaitingModal} onClose={() => setShowWaitingModal(false)} displayCount={tenantMatchCount} />
      {showGuestOnboarding && (
        <GuestOnboardingModal onClose={() => setShowGuestOnboarding(false)} />
      )}
      <BetaFeedbackButton />
    </div>
  );
}
